import { RefObject, useEffect, useState } from "react";

interface Args extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
}

export const useIntersectionObserver = (
  elementRef: RefObject<Element>,
  { threshold, root, rootMargin, freezeOnceVisible }: Args = {
    threshold: 0,
    root: null,
    rootMargin: "0%",
    freezeOnceVisible: true,
  }
): { entry?: IntersectionObserverEntry; isIntersecting: boolean } => {
  const [entry, setEntry] = useState<IntersectionObserverEntry>();

  const frozen = entry?.isIntersecting && freezeOnceVisible;

  const updateEntry = ([entry]: IntersectionObserverEntry[]): void => {
    setEntry(entry);
  };

  useEffect(() => {
    const node = elementRef?.current; // DOM Ref

    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || frozen || !node) return;

    const observerParams = { threshold, root, rootMargin };
    const observer = new IntersectionObserver(updateEntry, observerParams);

    observer.observe(node);

    return () => observer.disconnect();
  }, [
    elementRef,
    elementRef.current,
    JSON.stringify(threshold),
    root,
    rootMargin,
    frozen,
  ]);

  return { entry, isIntersecting: entry?.isIntersecting ?? false };
};

// Usage

// const Section = (props: { title: string }) => {
//   const ref = useRef<HTMLDivElement | null>(null)
//   const entry = useIntersectionObserver(ref, {})
//   const isVisible = !!entry?.isIntersecting

//   console.log(`Render Section ${props.title}`, { isVisible })

//   return (
//     <div ref={ref} >
//       <div style={{ margin: 'auto' }}>{props.title}</div>
//     </div>
//   )
// }

// export default function Component() {
//   return (
//     <>
//       {Array.from({ length: 5 }).map((_, index) => (
//         <Section key={index + 1} title={`${index + 1}`} />
//       ))}
//     </>
//   )
// }

// Related Links
// https://betterprogramming.pub/lazy-loading-in-next-js-simplified-435681afb18a
// https://usehooks-ts.com/react-hook/use-intersection-observer
